// AI difficulty profiles.
//
// Difficulty used to be four numbers — think cadence, aggression delay, resource reserve, and
// retreat threshold. Every difficulty played exactly the same game, just faster or slower, so
// Hard was Easy with the clock sped up rather than a better opponent.
//
// These profiles keep those four knobs and add *behavioural* switches on top, so the difficulties
// differ in what the AI is capable of, not only how quickly it reacts.
//
// Fairness is unchanged: no difficulty grants resources, reveals hidden information (there is no
// fog of war to hide any), or changes combat rules. Everything here is about decision quality
// and action rate.

import type { AiDifficulty } from '../content/factions';

export interface DifficultyProfile {
  /** seconds between each budgeted pass */
  cadence: { director: number; cards: number; squads: number };
  /** seconds of quiet before the watchdog forces a PRESSURE stance */
  aggressionDelay: number;
  /** power held back from discretionary spending */
  resourceReserve: number;
  /** squad health fraction that triggers an individual withdraw */
  retreatHealth: number;

  // ---- behavioural switches
  /**
   * Commands per second this director may issue. The single most important addition: without
   * it, Easy is merely slow to *think* but still executes instantly and perfectly once it does.
   * A budget makes low difficulties feel human-limited rather than just sluggish.
   */
  actionsPerSecond: number;
  /** how many separate attack groups may be in flight (1 = single-prong only) */
  maxProngs: number;
  /** gather an assault group at a staging point before committing, instead of trickling in */
  usesStaging: boolean;
  /** minimum squads gathered before an assault launches */
  stagingThreshold: number;
  /** retreat a whole engaged group when it is outmatched, not just individual hurt squads */
  usesGroupRetreat: boolean;
  /** group strength ratio below which a group-level retreat is ordered */
  groupRetreatRatio: number;
  /** how the card ladder weighs the enemy's army composition */
  counterPlay: 'off' | 'large-only' | 'full';
  /** seconds between refreshes of the enemy composition read */
  compositionRecheck: number;
  /** how many opening scripts this difficulty may draw from */
  openingVariety: number;
}

const PROFILES: Record<AiDifficulty, DifficultyProfile> = {
  // All values // TEST — they set strategic pressure, not combat rules, and want an owner pass.
  easy: {
    cadence: { director: 2, cards: 3, squads: 1 },
    aggressionDelay: 210,
    resourceReserve: 450,
    retreatHealth: 0.45,
    actionsPerSecond: 1,
    maxProngs: 1,
    usesStaging: false,
    stagingThreshold: 5,
    usesGroupRetreat: false,
    groupRetreatRatio: 0,
    counterPlay: 'off',
    compositionRecheck: 12,
    openingVariety: 1,
  },
  standard: {
    cadence: { director: 1, cards: 2, squads: 0.5 },
    aggressionDelay: 150,
    resourceReserve: 300,
    retreatHealth: 0.3,
    actionsPerSecond: 3,
    maxProngs: 1,
    usesStaging: true,
    stagingThreshold: 4,
    usesGroupRetreat: true,
    groupRetreatRatio: 0.6,
    counterPlay: 'large-only',
    compositionRecheck: 6,
    openingVariety: 3,
  },
  hard: {
    cadence: { director: 0.5, cards: 1, squads: 0.25 },
    aggressionDelay: 90,
    resourceReserve: 200,
    retreatHealth: 0.22,
    actionsPerSecond: 6,
    maxProngs: 2,
    usesStaging: true,
    stagingThreshold: 3,
    usesGroupRetreat: true,
    groupRetreatRatio: 0.75,
    counterPlay: 'full',
    compositionRecheck: 2,
    openingVariety: 4,
  },
};

export function difficultyProfile(difficulty: AiDifficulty): DifficultyProfile {
  return PROFILES[difficulty] ?? PROFILES.standard;
}
